import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Calendar as CalendarIcon, ExternalLink } from "lucide-react";

const REGIONS = ["", "East Africa", "West Africa", "Central Africa", "Southern Africa", "Pan-African", "MENA", "Other"];
const SECTORS = ["", "Infrastructure", "Energy & Climate", "Fintech", "Digital Infrastructure", "Telecom", "Industrials", "Agriculture", "Logistics", "Healthcare", "Mining", "Other"];
const RAISE_AMOUNTS = ["", "Under $1M", "$1-5M", "$5-10M", "$10-25M", "$25-50M", "$50-100M", "$100M+"];
const STAGES = ["", "Idea / Pre-revenue", "Early revenue", "Series A / Growth", "Series B+ / Scaling", "Pre-IPO / Pre-exit", "Established / Recap"];

function formatPrice(amount, isLeadOnly) {
  if (isLeadOnly) return "Custom";
  if (amount === 0) return "Free";
  return `$${(amount / 1000).toFixed(0)}k`;
}

export default function Intros() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [services, setServices] = useState([]);
  const [capitalStackOptions, setCapitalStackOptions] = useState([]);
  const [successFeeBands, setSuccessFeeBands] = useState({});
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    requester_name: "", requester_email: "", requester_org: "", requester_role: "",
    target_description: "", region: "", sector: "",
    raise_amount: "", current_stage: "", capital_stack: "", services_needed: [],
  });

  useEffect(() => {
    api.get("/public/intros/tiers").then((r) => {
      setTiers(r.data.tiers || []);
      setServices(r.data.services || []);
      setCapitalStackOptions(r.data.capital_stack_options || []);
      setSuccessFeeBands(r.data.success_fee_bands || {});
      setCalendlyUrl(r.data.calendly_url || "");
    });
  }, []);

  const toggleService = (svc) => {
    setForm((f) => ({
      ...f,
      services_needed: f.services_needed.includes(svc)
        ? f.services_needed.filter((s) => s !== svc)
        : [...f.services_needed, svc],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return toast.error("Pick an engagement first.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/intros/checkout", {
        ...form,
        tier: selected,
        origin_url: window.location.origin,
      });
      if (data.free) {
        const qs = new URLSearchParams({
          free: "1",
          intro_id: data.intro_id,
          ...(data.is_custom ? { custom: "1" } : {}),
          ...(data.calendly_url ? { calendly: data.calendly_url } : {}),
        });
        navigate(`/intros/thank-you?${qs.toString()}`);
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedTier = tiers.find((t) => t.id === selected);
  const isFreeOrCustom = selected === "free" || selected === "custom";

  return (
    <div className="bg-cream min-h-screen text-navy" data-testid="intros-page">
      <Toaster richColors position="top-right" />
      <SiteHeader />

      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-12">
        <div className="overline text-gold-deep mb-4">Expert Consultation</div>
        <h1 className="editorial-h1 text-5xl md:text-6xl text-navy">
          Raise <span className="italic">smarter.</span>
        </h1>
        <div className="gold-rule mt-6 mb-8" />
        <p className="text-navy/75 max-w-2xl font-light leading-relaxed">
          Joseph reviews your investment opportunity, identifies the right investors to target, then
          makes warm introductions personally. Fees scale with raise size and the depth of engagement.
          Paid tiers are authorised up-front via Stripe — and refunded in full if Joseph declines.
        </p>
      </section>

      {/* Tiers */}
      <section className="max-w-6xl mx-auto px-6 md:px-10" data-testid="intros-tiers">
        <div className="overline text-gold-deep mb-4">1 — Choose engagement</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiers.map((t) => {
            const active = selected === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelected(t.id)}
                data-testid={`tier-${t.id}`}
                className={`text-left bg-white border p-6 transition-all relative ${
                  active ? "border-gold shadow-lg -translate-y-1" : "border-navy/10 hover:border-navy/30"
                }`}
              >
                <div className="overline text-navy/60">{t.raise_band}</div>
                <div className="mt-2 font-serif text-3xl text-navy">
                  {formatPrice(t.amount, t.is_lead_only)}
                  {t.amount > 0 && <span className="text-sm text-navy/50 ml-1">USD</span>}
                </div>
                <div className="mt-1 text-sm font-medium text-gold-deep">{t.label}</div>
                <p className="mt-3 text-sm text-navy/70 leading-relaxed font-light">{t.description}</p>
                {active && (
                  <div className="mt-4 flex items-center gap-2 text-gold-deep text-xs uppercase tracking-widest">
                    <CheckCircle2 className="h-4 w-4" /> Selected
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Success-fee disclosure — applies to all tiers */}
      {Object.keys(successFeeBands).length > 0 && (
        <section
          className="max-w-6xl mx-auto px-6 md:px-10 mt-8"
          data-testid="success-fee-banner"
        >
          <div className="bg-white border-l-2 border-gold p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-start md:gap-8">
              <div className="md:w-1/3">
                <div className="overline text-gold-deep">Included in every tier</div>
                <h3 className="font-serif text-xl md:text-2xl text-navy mt-2">
                  Fundraise success fee.
                </h3>
              </div>
              <div className="md:w-2/3 mt-4 md:mt-0">
                <p className="text-sm text-navy/75 font-light leading-relaxed">
                  Every engagement also includes <strong>fundraise execution support</strong>.
                  A success fee of <strong>1–5%</strong> on capital actually raised applies in addition
                  to the up-front engagement fee — only charged on a successful close, and the band depends on the capital stack:
                </p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="success-fee-bands">
                  {Object.entries(successFeeBands).map(([stack, band]) => (
                    <div
                      key={stack}
                      data-testid={`fee-band-${stack.replace(/\W+/g, "-").toLowerCase()}`}
                      className="border border-navy/10 p-3 text-sm"
                    >
                      <div className="overline text-navy/60">{stack}</div>
                      <div className="font-serif text-xl text-navy mt-1">
                        {band.min}–{band.max}<span className="text-sm text-navy/50">%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-navy/55 mt-4 italic">
                  Exact percentage is agreed in writing per engagement and is contingent on funds being successfully raised.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Form */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 mt-16 pb-24">
        <div className="overline text-gold-deep mb-4">2 — Share your context</div>
        <h2 className="editorial-h2 text-3xl text-navy mb-2">Tell Joseph about the raise.</h2>
        <div className="gold-rule mt-6 mb-10" />

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="intros-form">
          {[
            ["requester_name", "Your name *", true],
            ["requester_email", "Your email *", true, "email"],
            ["requester_org", "Organisation"],
            ["requester_role", "Your role"],
          ].map(([k, l, req, type]) => (
            <div key={k}>
              <label className="overline text-navy/60">{l}</label>
              <input
                type={type || "text"}
                required={req}
                data-testid={`intros-input-${k}`}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
              />
            </div>
          ))}

          {[
            ["raise_amount", "Raise size", RAISE_AMOUNTS],
            ["current_stage", "Current stage", STAGES],
            ["capital_stack", "Capital stack", ["", ...capitalStackOptions]],
            ["region", "Region", REGIONS],
            ["sector", "Sector", SECTORS],
          ].map(([k, l, opts]) => (
            <div key={k}>
              <label className="overline text-navy/60">{l}</label>
              <select
                data-testid={`intros-input-${k}`}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
              >
                {opts.map((o) => <option key={o} value={o}>{o || "— select —"}</option>)}
              </select>
            </div>
          ))}

          {/* Services needed (checkbox grid) */}
          <div className="md:col-span-2">
            <label className="overline text-navy/60">Services needed (tick all that apply)</label>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="intros-services">
              {services.map((svc) => {
                const checked = form.services_needed.includes(svc);
                return (
                  <label
                    key={svc}
                    data-testid={`service-${svc.replace(/\W+/g, "-").toLowerCase()}`}
                    className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                      checked ? "border-gold bg-gold/5" : "border-navy/10 hover:border-navy/30"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleService(svc)}
                      className="h-4 w-4 accent-gold"
                    />
                    <span className="text-sm text-navy/85">{svc}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="overline text-navy/60">Anything else Joseph should know? *</label>
            <textarea
              required
              rows={5}
              data-testid="intros-input-target_description"
              value={form.target_description}
              onChange={(e) => setForm({ ...form, target_description: e.target.value })}
              placeholder="Describe the company, traction, use of funds and target investors. The more context, the better the guidance."
              className="w-full mt-2 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm"
            />
          </div>

          <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-navy/10">
            <div className="text-xs text-navy/60 max-w-md">
              {selected && selectedTier && (
                <>
                  {isFreeOrCustom
                    ? selected === "custom"
                      ? "Joseph will reach out personally within 5 business days to scope fee and timeline. No payment is taken now."
                      : "After submitting, you'll book a 20-min discovery call via Calendly. No payment is taken."
                    : `${formatPrice(selectedTier.amount, false)} USD is authorised via Stripe and refunded in full if Joseph declines.`}
                </>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting || !selected}
              data-testid="intros-submit"
              className="bg-navy text-gold px-7 py-3 text-xs uppercase tracking-widest hover:bg-navy-600 disabled:opacity-50"
            >
              {submitting
                ? "Processing…"
                : isFreeOrCustom
                  ? selected === "custom"
                    ? "Submit brief"
                    : "Submit & book call"
                  : "Continue to payment →"}
            </button>
          </div>
        </form>
      </section>

      <footer className="bg-navy text-cream/70 py-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-xs flex justify-between">
          <span>© {new Date().getFullYear()} Joseph Poche</span>
          <Link to="/" className="text-gold hover:underline">Back to site</Link>
        </div>
      </footer>
    </div>
  );
}

export function IntroThankYou() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const isFree = params.get("free") === "1";
  const isCustom = params.get("custom") === "1";
  const calendlyFromQuery = params.get("calendly") || "";

  const [status, setStatus] = useState(isFree ? "received" : "checking");
  const [attempts, setAttempts] = useState(0);
  const [calendlyUrl, setCalendlyUrl] = useState(calendlyFromQuery);

  useEffect(() => {
    if (isFree || !sessionId) return;
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/public/intros/status/${sessionId}`);
        if (data.calendly_url) setCalendlyUrl(data.calendly_url);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {
        /* network blip */
      }
      if (n >= 8) {
        setStatus("pending");
        return;
      }
      setTimeout(() => { setAttempts(n + 1); poll(n + 1); }, 2000);
    };
    poll(0);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isFree]);

  const showCalendly = (status === "paid" || status === "received") && calendlyUrl && !isCustom;

  return (
    <div className="bg-cream min-h-screen text-navy" data-testid="intros-thankyou">
      <SiteHeader />
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-24 text-center">
        <CheckCircle2 className="h-16 w-16 text-gold mx-auto" />
        <div className="overline text-gold-deep mt-6">
          {status === "checking" && "Verifying payment…"}
          {status === "pending" && "Payment processing"}
          {status === "paid" && "Payment authorised"}
          {status === "received" && (isCustom ? "Brief received" : "Request received")}
          {status === "expired" && "Session expired"}
        </div>
        <h1 className="editorial-h1 text-4xl md:text-5xl text-navy mt-4">
          {status === "expired" ? "Let's try again." : "Thank you."}
        </h1>
        <div className="gold-rule mx-auto mt-6 mb-8" />
        <p className="text-navy/75 font-light leading-relaxed">
          {status === "checking" && "Hang tight while we confirm with Stripe…"}
          {status === "pending" && "Your payment is still being processed. You'll receive a confirmation email when it completes."}
          {status === "paid" && "Your engagement is live. Please book your kickoff call below — Joseph will be in touch directly within 2 business days."}
          {status === "received" && !isCustom && "Your discovery request is in Joseph's queue. Please book a 20-minute discovery call below."}
          {status === "received" && isCustom && "Your brief is with Joseph. He'll reach out personally within 5 business days to scope fee, timeline and process."}
          {status === "expired" && "Your Stripe checkout session expired. You can submit a new request below."}
        </p>

        {/* Calendly booking CTA */}
        {showCalendly && (
          <div
            data-testid="calendly-block"
            className="mt-10 bg-navy text-cream p-6 border-l-2 border-gold text-left"
          >
            <div className="flex items-center gap-3">
              <CalendarIcon className="h-5 w-5 text-gold" />
              <div className="overline text-gold">Book your appointment</div>
            </div>
            <p className="mt-3 text-sm text-cream/85 font-light leading-relaxed">
              Pick a slot that works for you. Joseph will join via the Calendly invite link sent to your email.
            </p>
            <a
              href={calendlyUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="calendly-link"
              className="mt-5 inline-flex items-center gap-2 bg-gold text-navy px-5 py-3 text-xs uppercase tracking-widest hover:bg-gold-soft"
            >
              Open Calendly <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-cream/40">
              (Calendly link — simulated for now)
            </div>
          </div>
        )}

        <div className="mt-10 flex justify-center gap-3">
          <button
            onClick={() => navigate("/")}
            data-testid="thankyou-home"
            className="border border-navy text-navy px-6 py-3 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold"
          >
            Back to site
          </button>
          {status === "expired" && (
            <button
              onClick={() => navigate("/intros")}
              data-testid="thankyou-retry"
              className="bg-navy text-gold px-6 py-3 text-xs uppercase tracking-widest hover:bg-navy-600"
            >
              Try again
            </button>
          )}
        </div>
        {attempts > 0 && status === "checking" && (
          <div className="mt-6 text-xs text-navy/40">Attempt {attempts}/8</div>
        )}
      </div>
    </div>
  );
}
